import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Password, TextInput, Typography } from '@guildeducationinc/recess';
import ArrowBackIcon from '@guildeducationinc/recess/icons/ArrowBackIcon';
import { ClientHeader } from '@src/components/LoginForm/ClientHeader';
import { SIGN_UP } from '@src/graphql/queries/user';
import { useForgotPassword } from '@src/hooks/useForgotPassword';
import { getQueryParam, TrackableButton, useLocale } from 'pe-core-frontend';
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styles from '../LoginForm/LoginForm.module.scss';
export const ResetPassword = () => {
    const token = getQueryParam('token');
    const { t } = useLocale();
    const navigate = useNavigate();
    const location = useLocation();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmed, setConfirmed] = useState(token ? true : false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const { resetPassword, verifyConfirmationCode } = useForgotPassword();
    const [signUp] = useMutation(SIGN_UP);
    if (!token && !location.state?.email) {
        return _jsx(Navigate, { to: '/auth/login' });
    }
    const handleResendCode = async () => {
        try {
            await signUp({
                variables: {
                    email: location.state.email,
                },
            });
            setMessage(t('Confirmation code re-sent'));
        }
        catch (e) {
            setError(e.message);
        }
    };
    const handleVerifyConfirmationCode = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setError('');
        setMessage('');
        setConfirmed(false);
        if (!confirmationCode || confirmationCode.trim() === '') {
            setError(t('Confirmation code is required'));
            return;
        }
        const regex = /^\d{6}$/;
        if (!regex.test(confirmationCode.trim())) {
            setError(t('Confirmation code must be 6 digits'));
            return;
        }
        try {
            setSubmitting(true);
            const result = await verifyConfirmationCode(location.state.email, confirmationCode);
            if (result.data.verifyConfirmationCode) {
                setConfirmed(true);
            }
            else {
                setConfirmed(false);
                setError(t('Invalid confirmation code'));
            }
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setError('');
        setMessage('');
        if (!password || password.trim() === '') {
            setError(t('Password is required'));
            return;
        }
        try {
            setSubmitting(true);
            const result = await resetPassword(token ?? '', password, token ? '' : location.state.email, token ? '' : confirmationCode, true);
            if ('message' in result.data.resetPassword) {
                const { type, message } = result.data.resetPassword;
                if (type === 'error') {
                    setError(t(message));
                }
                else {
                    setSubmitted(true);
                    setMessage(t(message));
                    if (type === 'success') {
                        navigate('/');
                    }
                }
            }
            else if ('validationErrors' in result.data.resetPassword) {
                const message = result.data.resetPassword.validationErrors.password;
                setError(t(message));
            }
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx("div", { className: styles.loginForm, children: _jsxs("div", { className: styles.container, children: [_jsx(ClientHeader, {}), _jsxs("fieldset", { disabled: submitting, children: [!confirmed && (_jsxs("form", { noValidate: true, className: styles.form, onSubmit: handleVerifyConfirmationCode, children: [_jsx(Typography, { variant: 'h2', children: t("We've sent you a confirmation code") }), _jsx(Typography, { variant: 'body1', children: t('Confirmation code description') }), _jsx(Alert, { hideClose: true, display: !!message, type: 'success', className: 'margin--bottom_24', children: message }), _jsxs("div", { children: [_jsx(TextInput, { label: t('Enter the 6-digit code'), type: 'text', className: 'margin--bottom_24', value: confirmationCode, onChange: e => setConfirmationCode(e.target.value), required: true, errors: error }), _jsx(TrackableButton, { enText: 'Re-send code', tag: 'form', type: 'button', onClick: () => handleResendCode(), appearance: 'outline', children: t('Re-send code') })] }), _jsxs("div", { className: 'flex w-full items-center mt-5', children: [_jsx("div", { className: 'flex-grow text-left', children: _jsxs(TrackableButton, { btnType: 'link', enText: 'Previous', tag: 'form', component: Link, to: '/auth/sign-up', className: 'flex gap-1 flexItems--center', children: [_jsx(ArrowBackIcon, { size: 16 }), " ", t('Previous')] }) }), _jsx("div", { className: 'flex-grow text-right', children: _jsx(TrackableButton, { enText: 'Continue', tag: 'form', type: 'submit', children: t('Continue') }, 'submit') })] })] })), confirmed && (_jsxs("form", { className: styles.form, onSubmit: handleSubmit, children: [_jsx(Typography, { variant: 'h2', children: token ? t('Set Password') : t('Create your password') }), !submitted && (_jsx(Alert, { hideClose: true, display: true, type: 'info', className: 'margin--bottom_24', children: _jsx("div", { className: styles.passwordPolicy, children: t('Password policy details') }) })), _jsx(Alert, { hideClose: true, display: !!error, type: 'danger', className: 'margin--bottom_24', children: error }), _jsx(Alert, { hideClose: true, display: !!message, type: 'success', className: 'margin--bottom_24', children: message }), !submitted && (_jsx(Password, { style: { width: '100%' }, label: t('New Password'), value: password, onChange: e => setPassword(e.target.value) })), !submitted && (_jsxs("div", { className: 'flex flex-col flexItems--center mt-2', children: [_jsx(Typography, { variant: 'label2', children: token
                                                ? t("By clicking submit, you agree to Guild's")
                                                : t("By clicking sign up, you agree to Guild's") }), _jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'link', enText: 'Terms', tag: 'footer', href: 'https://guild.com/terms-of-use', target: '_blank', rel: 'noopener noreferrer', children: t('Terms') }), "\u00A0", t('and'), "\u00A0", _jsx(TrackableButton, { btnType: 'link', enText: 'Privacy policy', tag: 'footer', href: 'https://guild.com/privacy-policy', target: '_blank', rel: 'noopener noreferrer', children: t('Privacy policy') })] })] })), !submitted && (_jsxs("div", { className: 'flex w-full items-center mt-5', children: [_jsxs("div", { className: 'flex-grow text-left', children: [token && (_jsxs(TrackableButton, { btnType: 'link', enText: 'Back to log in', tag: 'form', component: Link, to: '/auth/sign-up', className: 'flex gap-1 flexItems--center', children: [_jsx(ArrowBackIcon, { size: 16 }), " ", t('Back to log in')] })), !token && (_jsxs(TrackableButton, { btnType: 'link', enText: 'Previous', tag: 'form', component: Link, onClick: e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setConfirmed(false);
                                                    }, className: 'flex gap-1 flexItems--center', children: [_jsx(ArrowBackIcon, { size: 16 }), " ", t('Previous')] }))] }), _jsx("div", { className: 'flex-grow text-right', children: _jsx(TrackableButton, { enText: token ? 'Submit' : 'Sign up', tag: 'form', type: 'submit', children: token ? t('Submit') : t('Sign up') }, 'submit') })] })), submitted && (_jsx("div", { className: 'flex flexJustify--center', children: _jsxs(TrackableButton, { enText: 'Back to log in', tag: 'form', type: 'button', onClick: () => navigate('/auth/login'), className: 'flex gap-1 flexItems--center', children: [_jsx(ArrowBackIcon, { size: 16 }), " ", t('Back to log in')] }) }))] }))] })] }) }));
};
